"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Embedded auth (ADR-014): sign-up/sign-in/verify/reset rendered as an
 * in-page dialog on the landing route — never a redirect to a separate
 * Cognito Hosted-UI window. Radix's Dialog already closes on Escape and
 * traps focus, satisfying the shell's keyboard/focus baseline.
 */

import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useI18n } from "@/i18n"
import { atmosphereFor } from "@/lib/atmosphere"
import type { AuthMode } from "./authMode"
import { SignUpForm } from "./SignUpForm"
import { VerifyForm } from "./VerifyForm"
import { SignInForm } from "./SignInForm"
import { ForgotPasswordForm } from "./ForgotPasswordForm"
import { ResetPasswordForm } from "./ResetPasswordForm"

interface AuthPanelProps {
  open: boolean
  initialMode: AuthMode
  onOpenChange: (open: boolean) => void
  onAuthenticated: () => void
}

const TITLE_KEYS: Record<AuthMode, string> = {
  signIn: "auth.logInTitle",
  signUp: "auth.signUpTitle",
  verify: "auth.verifyTitle",
  forgot: "auth.forgotTitle",
  reset: "auth.resetTitle",
}

export function AuthPanel({ open, initialMode, onOpenChange, onAuthenticated }: AuthPanelProps) {
  const { t } = useI18n()
  const [mode, setMode] = useState<AuthMode>(initialMode)
  const [pendingEmail, setPendingEmail] = useState("")

  function handleOpenChange(next: boolean) {
    if (next) {
      setMode(initialMode)
    }
    onOpenChange(next)
  }

  function handleModeChange(nextMode: AuthMode, email?: string) {
    if (email !== undefined) setPendingEmail(email)
    setMode(nextMode)
  }

  // T1 THRESHOLD (ADR-070 category B, founder-approved 2026-09-11): the same frame
  // the mobile welcome screen uses, as a band at the top of this dialog. Sign-in
  // holds no plan, recipe or ingredient, so there is nothing here a photograph
  // could be read as depicting. Decorative — the form is the content. The band is
  // absent when the registry has no asset, and the dialog is unchanged without it.
  // ADR-014 is untouched: this is still the embedded modal, not a redirect or a page.
  const threshold = atmosphereFor("auth_threshold")

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="overflow-hidden rounded-bb-xl border-line bg-paper p-0 sm:max-w-md">
        {threshold && (
          <div
            aria-hidden="true"
            className="relative h-[150px] w-full bg-cream bg-cover bg-center"
            style={{ backgroundImage: `url(${threshold.asset})` }}
          >
            {/* the dialog's close control sits at top-right, over the photograph —
                a soft corner wash keeps it readable whatever the frame does there */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(9,26,19,0.45),transparent_45%)]" />
          </div>
        )}
        <div className="flex flex-col gap-4 p-6 pt-5">
        <DialogHeader>
          <DialogTitle className="text-ink">{t(TITLE_KEYS[mode])}</DialogTitle>
        </DialogHeader>

        {mode === "signUp" && <SignUpForm onModeChange={handleModeChange} />}
        {mode === "verify" && <VerifyForm email={pendingEmail} onModeChange={handleModeChange} />}
        {mode === "signIn" && (
          <SignInForm onModeChange={handleModeChange} onSuccess={onAuthenticated} />
        )}
        {mode === "forgot" && (
          <ForgotPasswordForm initialEmail={pendingEmail} onModeChange={handleModeChange} />
        )}
        {mode === "reset" && (
          <ResetPasswordForm email={pendingEmail} onModeChange={handleModeChange} />
        )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
